import { Calendar, Select, Text } from "@ttc3k/trekker";
import {
  BookerManyQuery,
  EnumProductType,
  useInventoryByProductBookedDatesQuery,
} from "gql/generated";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Flex, Grid } from "styled-system/jsx";
import { ManualOrderFormValues } from "./useManualOrderForm";
import {
  Collapsible,
  ControlledInput,
  SimpleSelectableCard,
} from "shared/components";
import { convertDateRangeToCalendarValue } from "widgets/booker/features/OverlayPages/DatesPage/helpers";
import {
  formatToISO8601,
  getMultilingualString,
  parseCalendarValueAsString,
} from "shared/utils";
import { useTranslation } from "react-i18next";
import { getProductTypeConfig } from "shared/config/productTypeConfig";

type ManualOrderFormComponentsProps = {
  bookerOptions: {
    label: string;
    value: string;
    type: EnumProductType;
  }[];
  allBookers: BookerManyQuery["bookerMany"];
  isForSpecificPerson: boolean;
};

export const ManualOrderFormComponents = ({
  bookerOptions,
  allBookers,
  isForSpecificPerson,
}: ManualOrderFormComponentsProps) => {
  const { control, setValue, formState } =
    useFormContext<ManualOrderFormValues>();

  const { t } = useTranslation("storefront", {
    keyPrefix: "CALENDAR.MANUAL_ORDER.FORM",
  });
  const bookerId = useWatch({ control, name: "bookerId" });
  const productType = useWatch({ control, name: "mainProductType" });
  const startISO = useWatch({ control, name: "startISO" });
  const endISO = useWatch({ control, name: "endISO" });
  const selectedUpsellProductIds = useWatch({
    control,
    name: "upsellProductIds",
  });
  const feeOptions = bookerOptions.filter(v => v.type === EnumProductType.Fee);
  const accommodationOptions = bookerOptions.filter(
    v => v.type === EnumProductType.Accommodation,
  );
  const bookerData = useMemo(
    () => allBookers.find(b => b._id === bookerId),
    [allBookers, bookerId],
  );
  const productTypeConfig = getProductTypeConfig(
    productType as EnumProductType | null | undefined,
  );
  const mainProduct = useMemo(
    () => bookerData?.bundles[0]?.product,
    [bookerData],
  );

  const { data: invData } = useInventoryByProductBookedDatesQuery({
    variables: { productId: mainProduct?._id },
    skip: !mainProduct?._id,
  });

  const upsellProducts = useMemo(
    () => bookerData?.bundles[0]?.upsells ?? [],
    [bookerData],
  );

  const handleSelectBooker = (id: string) => {
    setValue("bookerId", id);
    const newProductType =
      bookerOptions.find(p => p.value === id)?.type ?? EnumProductType.Fee;
    setValue("mainProductType", newProductType);
    handleClearDates();
  };

  const handleClearDates = () => {
    setValue("startISO", "");
    setValue("endISO", "");
  };

  const handleDateValueChange = (dates: string[]) => {
    if (dates.length > 1) {
      setValue("startISO", dates[0]);
      setValue("endISO", dates[1]);
    } else if (dates.length === 1) {
      setValue("startISO", dates[0]);
      setValue("endISO", "");
    } else {
      handleClearDates();
    }
  };

  const handleUpsellSelect = (id: string) => {
    const isIdSelected = selectedUpsellProductIds.find(i => i === id);
    if (isIdSelected) {
      const newUpsellIds = selectedUpsellProductIds.filter(i => i !== id);
      setValue("upsellProductIds", newUpsellIds);
    } else {
      const newUpsellIds = [...selectedUpsellProductIds, id];
      setValue("upsellProductIds", newUpsellIds);
    }
  };

  const collection = Select.createCollection({ items: bookerOptions });

  return (
    <Flex flexDir={"column"} gap={"250"} minH={"230px"}>
      <Flex flexDir={"column"} gap={"250"}>
        <Select.Root
          collection={collection}
          value={[bookerId.toString()]}
          placeholder={t("BOOKING_WIDGET_PLACEHOLDER")}
          label={t("BOOKING_WIDGET")}
          onValueChange={({ value }: { value: string[] }) =>
            handleSelectBooker(value[0])
          }
        >
          {accommodationOptions.length > 0 && (
            <>
              <Select.ItemGroup>
                <Select.ItemGroupLabel>
                  {t("ACCOMMODATIONS")}
                </Select.ItemGroupLabel>
                {accommodationOptions.map(item => (
                  <Select.Item key={item.value} item={item} />
                ))}
              </Select.ItemGroup>
            </>
          )}
          {feeOptions.length > 0 && (
            <>
              <Select.ItemGroup>
                <Select.ItemGroupLabel>{t("FEES")}</Select.ItemGroupLabel>
                {feeOptions.map(item => (
                  <Select.Item key={item.value} item={item} />
                ))}
              </Select.ItemGroup>
            </>
          )}
        </Select.Root>
        <Grid
          gridTemplateColumns={"repeat(2, 1fr)"}
          gridGap={"200"}
          css={{
            "& > :nth-last-child(1):nth-child(odd)": {
              gridColumn: "1 / -1",
            },
          }}
        >
          <ControlledInput name={"firstName"} label={t("FIRST_NAME")} />
          <ControlledInput name={"lastName"} label={t("LAST_NAME")} />
          {/* Disable the email input if creating a manual order for a specific person */}
          <ControlledInput
            name={"email"}
            label={t("EMAIL")}
            disabled={isForSpecificPerson}
          />
        </Grid>
      </Flex>

      <Collapsible open={productTypeConfig.hasDates}>
        <Box width={"full"} height={"360px"} mt={"250"}>
          <Calendar
            unavailableDatesISO={formatToISO8601(
              invData?.inventoryDatesByProduct?.bookedDatesISO ?? [],
            )}
            checkOutOnlyDatesISO={formatToISO8601(
              invData?.inventoryDatesByProduct?.checkOutOnlyDatesISO ?? [],
            )}
            onValueChange={({ valueAsString }: { valueAsString: string[] }) =>
              handleDateValueChange(parseCalendarValueAsString(valueAsString))
            }
            value={convertDateRangeToCalendarValue({
              startDateISO: startISO,
              endDateISO: endISO,
            })}
            onClearDatesClick={handleClearDates}
            displayJumpToToday
          />
        </Box>
        {formState.errors["startISO"] && (
          <Text visual={"smallSemiBold"} color={"text.error.dark"} mt={"150"}>
            {formState.errors["startISO"].message}
          </Text>
        )}
      </Collapsible>

      <Collapsible open={upsellProducts.length > 0}>
        <Flex flexDir={"column"} gap={"200"} mt={"250"}>
          <Flex flexDir={"column"} gap={"100"}>
            <Text visual={"bodySemiBold"}>{t("UPSELLS")}</Text>
            <Text color={"text.mid"}>{t("UPSELLS_DESCRIPTION")}</Text>
          </Flex>
          <Grid gridTemplateColumns={"repeat(2, 1fr)"} gap={"150"}>
            {upsellProducts.map(upsell => (
              <SimpleSelectableCard
                key={upsell._id}
                width={"full"}
                type={"button"}
                onClick={() => handleUpsellSelect(upsell._id)}
                selected={selectedUpsellProductIds.includes(upsell._id)}
                label={getMultilingualString(upsell.name)}
              />
            ))}
          </Grid>
        </Flex>
      </Collapsible>
    </Flex>
  );
};
